'use client';

import { useRef } from 'react';
import 'keen-slider/keen-slider.min.css';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useGSAP } from '@gsap/react';
import initAnimation from './animations/scroll-lines-animation';

function ScrollLines({
    enableGap,
    dataI18nFileName,
    locale,
}: {
    enableGap: boolean;
    dataI18nFileName: string;
    locale: string;
}) {
    const { t } = useTranslation(dataI18nFileName);

    const leftList = t('left', { returnObjects: true }) as string[];
    const rightList: { id: string; title: string }[] = t('right', {
        returnObjects: true,
    }) as { id: string; title: string }[];

    const rootRef = useRef<HTMLDivElement>(null);
    const leftText = useRef<HTMLDivElement>(null);
    const rightTextContainer = useRef<HTMLDivElement>(null);
    const rightText = useRef<HTMLDivElement>(null);

    useGSAP(
        () => {
            return initAnimation(
                rootRef.current!,
                leftText.current!,
                rightTextContainer.current!,
                rightText.current!,
            );
        },
        { scope: rootRef },
    );

    return (
        <section
            ref={rootRef}
            className="flex w-full flex-col items-center"
            style={{ height: 'var(--homepage-vh, 100svh)' }}
        >
            <div className="tablet:max-w-[87.5vw] desktop:max-w-[1440px] flex h-full w-[87.5vw] max-w-[480px] flex-col justify-center">
                <div
                    className={clsx(
                        'flex h-full w-full flex-col justify-between gap-[50px] pt-[80px] sm:flex-row sm:gap-0 sm:pt-[160px]',
                    )}
                >
                    <div
                        ref={leftText}
                        className={clsx(
                            'j-h4 tablet:j-h2 flex h-auto w-full flex-col text-left text-[#06060A] sm:h-full sm:w-3/5',
                        )}
                    >
                        <p className="hidden">{leftList.join('')}</p>

                        {leftList.map((line, index) => (
                            <span key={index} className="block w-full sm:w-4/5">
                                {line.split(/\*\*(.*?)\*\*/g).map((part, i) =>
                                    i % 2 === 1 ? (
                                        <span
                                            key={i}
                                            style={{
                                                background:
                                                    'linear-gradient(90deg, #5C76FF 0%, #AD5CFF 93.24%)',
                                                WebkitTextFillColor:
                                                    'transparent',
                                                WebkitBackgroundClip: 'text',
                                                backgroundClip: 'text',
                                            }}
                                        >
                                            {part}
                                        </span>
                                    ) : (
                                        part
                                    ),
                                )}
                            </span>
                        ))}
                    </div>

                    <div
                        ref={rightTextContainer}
                        className={clsx(
                            'j-h7 relative h-full w-full flex-col overflow-hidden sm:h-4/5 sm:w-2/5',
                        )}
                    >
                        <div
                            ref={rightText}
                            className="tablet:gap-[160px] desktop:gap-[200px] j-h7 flex w-full flex-col gap-[120px]"
                        >
                            {rightList.map((item, index) => (
                                <div
                                    id={'right-' + item.id}
                                    key={'right-' + item.id}
                                    className={clsx(
                                        'flex flex-col text-left text-[#0D0D10CC]',
                                        locale !== 'zh' && enableGap
                                            ? 'gap-[8px]'
                                            : '',
                                    )}
                                >
                                    {item.title
                                        .split('\n')
                                        .reduce<string[][]>((acc, line, i) => {
                                            if (i % 2 === 0) acc.push([line]);
                                            else acc[acc.length - 1].push(line);
                                            return acc;
                                        }, [])
                                        .map((group, groupIndex) => (
                                            <p
                                                key={groupIndex}
                                                className="inline"
                                            >
                                                {group.map(
                                                    (line, lineIndex) => (
                                                        <span key={lineIndex}>
                                                            {line
                                                                .split(
                                                                    /\*\*(.*?)\*\*/g,
                                                                )
                                                                .map(
                                                                    (
                                                                        part,
                                                                        i,
                                                                    ) =>
                                                                        i %
                                                                            2 ===
                                                                        1 ? (
                                                                            <span
                                                                                key={
                                                                                    i
                                                                                }
                                                                                style={{
                                                                                    color: '#724DF9',
                                                                                }}
                                                                            >
                                                                                {
                                                                                    part
                                                                                }
                                                                            </span>
                                                                        ) : (
                                                                            part
                                                                        ),
                                                                )}
                                                            {lineIndex <
                                                                group.length -
                                                                    1 && (
                                                                <br
                                                                    className={clsx(
                                                                        dataI18nFileName ===
                                                                            'physical-ai' &&
                                                                            locale ===
                                                                                'zh' &&
                                                                            index ===
                                                                                0
                                                                            ? 'desktop:block hidden'
                                                                            : '',
                                                                    )}
                                                                />
                                                            )}
                                                        </span>
                                                    ),
                                                )}
                                            </p>
                                        ))}
                                </div>
                            ))}
                            <br />
                            <br />
                        </div>

                        <div
                            className="absolute bottom-0 left-0 right-0 h-1/4 w-full"
                            style={{
                                backgroundImage: `-webkit-linear-gradient(top, hsla(0,0%,100%,0), rgb(255, 255, 255)`,
                            }}
                        ></div>
                        <div
                            className="absolute top-0 h-1/4 w-full"
                            style={{
                                backgroundImage: `-webkit-linear-gradient(top, rgb(255, 255, 255), hsla(0,0%,100%,0))`,
                            }}
                        ></div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default ScrollLines;
